import { EventEmitter } from 'events';
import { DateTime } from "luxon";
import axios from 'axios';
import flatten from 'lodash/flatten';
import get from 'lodash/get';

import Http from './Http';

/**
 * @classdesc helper methods for HTTP
 */
export default class ApiBase extends EventEmitter {
    /**
     * @method getStatus
     * @param {number|number[]} successStatus
     * @return {number[]}
     */
    getStatus = (successStatus) => {
        return !Array.isArray(successStatus) ? [successStatus] : successStatus;
    };

    /**
     * @method handleResponse
     * @param {object} response
     * @param {object} error
     * @param {number} successStatus
     * @return {boolean|{status: number}}
     */
    handleResponse = (response, error, successStatus = 200) => {
        const status = this.getStatus(successStatus);

        if (status.indexOf(response.status) !== -1) {
            return {
                status: response.status,
                data: response.data,
            };
        }

        if (error) {
            return { status: error.response.status, ...error.response.data };
        }

        return { status: response.status, ...response.data };
    };

    /**
     * @method urlParams
     * @param {object} params
     * @return {string}
     */
    urlParams = (params) => {
        if (params === undefined || params.length === 0) {
            return '';
        }

        const queryStringItems = [];

        for (const field in params) {
            if (params[field] !== undefined && params[field] !== null &&
                params[field].length !== 0) {
                queryStringItems.push(field + '=' + params[field]);
            }
        }

        return '?' + queryStringItems.join('&');
    };

    /**
     * @method post
     * @param {string} resource
     * @param {object | []} data
     * @param {string | null} [event]
     * @param {number} [successStatus]
     * @param {object} config
     * @return {Promise<*>}
     */
    post = async (resource, data, event, successStatus = 201, config = {}) => {
        let err = null;

        const response = await Http.post(resource, data, config).catch(
            (error) => (err = error),
        );

        if (
            event !== undefined && event !== null &&
            this.getStatus(successStatus).indexOf(response.status) !== -1
        ) {
            this.emit(event, response);
        }

        return this.handleResponse(response, err, successStatus);
    };

    /**
     * @method patch
     * @param {string} resource
     * @param {object | []} data
     * @param {string | null} [event]
     * @return {Promise<boolean|{status: number}>}
     */
    patch = async (resource, data, event) => {
        let err = null;

        const response = await Http.patch(resource, data).catch(
            (error) => (err = error),
        );

        if (event !== undefined && event !== null && response.status === 200) {
            this.emit(event, response);
        }

        return this.handleResponse(response, err);
    };

    /**
     * @method get
     * @param {string} resource
     * @param {object} [data]
     * @return {Promise<*>}
     */
    get = async (resource, data) => {
        let err = null;

        const response = await Http.get(resource + this.urlParams(data)).catch(
            (error) => (err = error),
        );

        return this.handleResponse(response, err);
    };

    /**
     * @method delete
     * @param {string} resource
     * @param {object | null} [data]
     * @param {string | null} [event]
     * @param {number} [successStatus]
     * @return {Promise<*>}
     */
    delete = async (resource, data, event, successStatus = 204) => {
        let err = null;

        const response = await Http.delete(resource, data).catch(
            (error) => (err = error),
        );

        if (event !== undefined && event !== null && response.status === successStatus) {
            this.emit(event, response);
        }

        return this.handleResponse(response, err);
    };

    /**
     * @method requestUpload
     * @param {string} contentType
     * @param {string} visibility
     * @param {array} metadata
     */
    requestUpload = async (
        contentType,
        visibility = 'public-read',
        metadata) => {
        const request = await Http.post('uploads', {
            content_type: contentType,
            visibility,
            metadata,
        });

        return request.data.data;
    };

    /**
     * @method uploadFile
     * @param {UploadFile|Blob|any} contents
     * @param {string} permission
     * @param {string|null} type
     */
    uploadFile = async (contents, permission = 'public-read', type = null) => {
        const data = {};

        if (contents instanceof File || contents instanceof Blob) {
            type = contents.type;

            if (contents instanceof File) {
                data['OriginalName'] = contents.name;
            }
        }

        const upload = await this.requestUpload(type, permission, data);
        const uploadHeaders = upload.headers;
        const headers = { Host: '' };

        for (const h in uploadHeaders) {
            if (!uploadHeaders.hasOwnProperty(h)) continue;

            if (h === 'Content-Type') {
                headers[h] = uploadHeaders[h];
                continue;
            }

            headers[h] = uploadHeaders[h][0] || '';
        }

        let host = '';
        if ('Host' in uploadHeaders) {
            host = headers.Host;
            delete headers.Host;
        }

        let err;

        const request = await axios.put(upload.url, contents, {
            headers: headers,
        }).catch((e) => err = e);

        if (request.status !== 200) {
            return this.handleResponse(request, err);
        }

        return {
            status: request.status,
            host,
            key: upload.uuid,
            tempKey: upload.key,
        };
    };

    /**
     * @method prepareForRequest
     * @param data
     * @param config
     * @returns {{}}
     */
    prepareForRequest = (data, config) => {
        const formatted = {};

        flatten(config).forEach((field) => {
            let value = get(data, field.name);

            if (!value) {
                return;
            }

            switch (field.type) {
                case 'date':
                    value = (typeof value === 'string' ? DateTime.fromISO(value) : DateTime.fromJSDate(value)).toFormat("yyyy-MM-dd");
                    break;
                case 'select':
                    value = field.multiple ? value?.map(v => v.value) : (value?.value ?? null);
                    break;
            }

            formatted[field.name] = value;
        });

        return formatted;
    };

    downloadFromSignedUrl = async (id, filename) => {
        const { data, status } = await this.get(this.getDownloadUrl(id));

        if (status !== 200) {
            return;
        }

        return this.downloadFile(data.url, filename ?? data.filename);
    };

    downloadFile = async (url, filename = null) => {
        const { data, status, headers } = await axios({
            url,
            method: 'GET',
            responseType: 'blob'
        });

        if (status !== 200) {
            return;
        }

        const fileURL = window.URL.createObjectURL(new Blob([data]));
        const fileLink = document.createElement('a');

        if (!filename) {
            filename = `${ headers['content-disposition'].split("filename=")[1] }`;
        }

        fileLink.href = fileURL;
        fileLink.setAttribute('download', filename?.trim());
        document.body.appendChild(fileLink);

        fileLink.click();
    };
}
